<template>
  <div :class="$style.container">
    <Header title="Applicants Ranked" subtitle="Use the grading algorithm to assign application status">

      <template v-if="!loading && user.roles.admin">
        <Button @click="getProposedApplicationStatus(false)">
          View Proposed Application Status
        </Button>

        <Button @click="getProposedApplicationStatus(true)">
          View Proposed Application Status w/ ending waitlist
        </Button>

        <Button @click="assignApplicationStatus(false)">
          [DANGER] ASSIGN APPLICATION STATUS
        </Button>

        <Button @click="assignApplicationStatus(true)">
          [DANGER] END WAITLIST + ASSIGN APPLICATION STATUS
        </Button>
      </template>
    </Header>

    <Input
      :class="$style.item"
      name="search"
      label="Search"
      placeholder="Search for applicant"
      v-model="text"
    />
    <template v-if="data.length > 0">
      <VirtualList
        :class="$style.list"
        :data-component="ApplicantCard"
        :data-sources="filteredData"
        :estimate-size="130"
        :extra-props="{ base: `/app` }"
        :item-class="$style.item"
        :wrap-class="$style.wrap"
        data-key="_id"
      />
      <TextComponent align="center" as="p" type="p">
        Downloaded:
        {{ data.length }}
      </TextComponent>
    </template>
    <div v-else>
      <TextComponent align="center" as="h3" type="heading3"
        >No results found :(
      </TextComponent>
    </div>

  </div>
</template>

<script>
  import Button from '@hackthe6ix/vue-ui/Button';
  import Input from '@hackthe6ix/vue-ui/Input';
  import Select from '@hackthe6ix/vue-ui/Select';
  import Stack from '@hackthe6ix/vue-ui/Stack';
  import Text from '@hackthe6ix/vue-ui/Text';
  import Textarea from '@hackthe6ix/vue-ui/Textarea';
  import stringSimilarity from 'string-similarity';
  import swal from 'sweetalert';
  import 'vue-awesome/icons/filter';
  import vueJsonEditor from 'vue-json-editor';
  import VirtualList from 'vue-virtual-scroll-list';
  import ApplicantCard from '../../../containers/ApplicantCard';
  import Card from '../../../containers/Card';
  import Header from '../../../containers/Header';
  import { assignApplicationStatus, getRanks } from '../../../utils/api';
  import { getUser } from '../../../utils/UserController';

  export default {
  name: 'ApplicantsRanked',
  data() {
    return {
      ApplicantCard,
      data: [],
      filteredData: [],
      text: '',
      user: {
        roles: {}
      },
      loading: true,
    };
  },
  components: {
    ApplicantCard,
    Button,
    Card,
    VirtualList,
    Select,
    Stack,
    Header,
    Input,
    Textarea,
    TextComponent: Text,
    vueJsonEditor,
  },
  watch: {
    data(d) {
      this.filteredData = d;
    },
    text(t) {
      this.filteredData = this.data.map((user, index) => ({
        ...user,
        index: index,
        similarity: t ? stringSimilarity.compareTwoStrings(t, user.fullName) : 1
      })).sort((a, b) => b.similarity - a.similarity).filter((user) => user.similarity > 0.1);
    }
  },
  methods: {
    updateLocalApplicationStatus(data) {
      const { dead, accepted, rejected, waitlisted } = data;

      const newUsers = dead
      .concat(accepted)
      .concat(rejected)
      .concat(waitlisted)
      .sort((a, b) => {
        const diff = b?.internal?.computedFinalApplicationScore - a?.internal?.computedFinalApplicationScore;

        if (diff === 0) {
          const diffPersonal = b?.internal?.computedApplicationScore - a?.internal?.computedApplicationScore;

          if (diffPersonal === 0) {
            // If the scores are the same, the tiebreaker is whoever submitted earlier
            return a?.hackerApplication?.lastUpdated - b?.hackerApplication?.lastUpdated;
          } else {
            return diffPersonal;
          }
        } else {
          return diff;
        }
      });

      for (let i = 0; i < newUsers.length; i++) {

        if (this.data[i]._id === newUsers[i]._id) {
          this.data[i].status = {
            ...newUsers[i].status,
            internalTextStatus: `${this.data[i].status.internalTextStatus.split(' -> ')[0]} -> ${newUsers[i].status.internalTextStatus}`
          }
        } else {
          swal('Error', `There was a mismatch at index ${i} between ${ this.data[i]._id } and ${ newUsers[i]._id }`, 'error');
          return
        }
      }
    },
    async assignApplicationStatus(endWaitlist) {

      swal({
        title: `CONFIRM ACTION - Assign Application${ endWaitlist ? ' AND END THE WAITLIST' : ''}`,
        text: 'Are you sure you want to assign the application status? This action CANNOT be easily rolled back! Note that the current scores will immediately be used to assign admission status, even if users have not been completely graded. ' +
              'Please ensure the proposed application status looks good before proceeding.',
        icon: 'warning',
        buttons: true,
        dangerMode: true,
      }).then(async (confirm) => {

        const confirmationMessage = `Yes, I want to assign application status with end waitlist being ${ endWaitlist ? 'true' : 'false' }`;

        if (confirm && prompt(`Please enter the following message to confirm: "${ confirmationMessage }". This is the final warning!`) === confirmationMessage) {
          const result = await assignApplicationStatus(true, endWaitlist);

          if (result.success) {

            const { dead, accepted, rejected, waitlisted } = result.data;

            swal('Success',
              `Application status have been assigned. Please refresh the page to view changes. Accepted: ${accepted.length} Rejected: ${rejected.length} Waitlisted: ${waitlisted.length} Dead: ${dead.length}`,
              'success');

            return this.updateLocalApplicationStatus(result.data);

          } else {
            swal('Error', result.data, 'error');

          }
        }


        swal('Operation Aborted', 'User did not confirm action', 'error');
      });

    },
    async getProposedApplicationStatus(endWaitlist) {
      const result = await assignApplicationStatus(false, endWaitlist);

      if (result.success) {

       this.updateLocalApplicationStatus(result.data);

      } else {
        swal('Error', result.data, 'error');
      }

    },
    async fetchQuery() {
      const result = await getRanks();

      if (result.success) {
        this.loading = false;
        this.data = result.data;
      } else {
        swal('Error', result.data, 'error');
      }
    },
  },
  beforeMount() {
    (async () => {
      this.user = await getUser();

      swal({
        title: 'CONFIRM ACTION',
        text: 'Are you sure you want to view the ranked applicants? This is a computational intensive action that may result in severe server stress.',
        icon: 'warning',
        buttons: true,
        dangerMode: true,
      }).then((confirm) => {
        if (confirm) {
          this.fetchQuery(true);
        } else {
          this.$router.push('/');
        }
      });
    })();
  }
};
</script>

<style lang="scss" module src="./ApplicantsRanked.module.scss" />
